import { useEffect, useState } from "react";
import { fetchConfigBranding } from "@/lib/api";

interface UseBrandingResult {
  logoUrl: string | null;
  loading: boolean;
}

/**
 * Backs the topbar's client logo (ClientLogo) with the same real
 * `fetchConfigBranding` call `features/settings/settings.tsx`'s Org Profile
 * tab already uses — no new endpoint, no fixture, no local-only upload
 * store. A denied/erroring fetch just falls back to the org-initials
 * avatar (same treatment as no logo configured), rather than surfacing a
 * second error state for what is a decorative header element.
 */
export function useBranding(): UseBrandingResult {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchConfigBranding()
      .then((result) => {
        if (!cancelled) setLogoUrl(result.item?.logoUrl ?? null);
      })
      .catch(() => {
        // `fetchDetail` resolves with `{ item: null, error }` for an HTTP
        // failure, but a transport-level one (offline, DNS, a dropped
        // connection) rejects outright. Settle to "no logo" — the same
        // org-initials fallback as no logo configured — instead of letting
        // the rejection escape unhandled from an effect nobody awaits.
        if (!cancelled) setLogoUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { logoUrl, loading };
}
